import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { AxiosService } from 'src/axios/axios.service';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { useFeatureFlagsContext } from 'src/providers/FeatureFlagsProvider/FeatureFlags.context';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import { INACTIVE_PROFILING_STATE, ProfilingProgressContext } from './ProfilingProgress.context';
import type {
  tProfilingProgressContext,
  tProfilingProgressProviderProps,
  tProfilingState,
} from './ProfilingProgress.types';

type tProfilingStatusResponse = {
  active: boolean;
  total: number;
  profiled: number;
  classified: number;
  failed: number;
  funnel_id: string | null;
};

type tSyncStatusResponse = {
  result: {
    sync_type: string;
    sync_status: string;
    percentage: number;
    workspace_id: string;
    last_chunk_received_at?: string | null;
    updated_at?: string;
    created_at?: string;
    /** Meta's code for why the history sync failed; absent means unknown. */
    error_code?: number;
  };
};

const THROTTLE_MS = 200;
const HISTORY_TICK_MS = 2000;
const HISTORY_PROGRESS_CAP = 60;
const SYNC_POLL_MS = 15_000;
// RET-1394: hard ceiling on the history sync poll. The history phase has no
// terminal signal of its own when Meta delivers no WhatsApp Cloud-API history
// for a WABA — no HISTORY `sync_statuses` row is ever created, so the sync
// never COMPLETEs and `chunks.complete` never fires. Without a ceiling the
// navbar bar sits at 60% forever. The guard fires once attempts exceed this,
// i.e. 8 HTTP polls (ticks 1–8) + 1 ceiling-trigger tick ≈ 2m15s max.
const MAX_SYNC_POLLS = 8;
// RET-1511 follow-up: a HISTORY sync that hasn't received a chunk in this long
// is frozen (Meta stopped or never delivered), not actually in progress — real
// Cloud-API history arrives within minutes. On mount we skip the history phase
// for such stale rows so the bar stops reappearing on every reload; the poll
// ceiling above remains the safety net for rows that freeze mid-session.
const HISTORY_STALE_MS = 60 * 60 * 1000;
// RET-1975: bounded re-check for a sync that starts right after mount. The
// post-onboarding redirect (RET-1386) races the creation of the HISTORY
// sync row (measured: the last mount-time fetch missed it by 120ms), and no
// history-start socket event exists — most WAPI connects have no CONTACT
// phase, so nothing else can turn the indicator on until a reload. While the
// mount fetches find no active run, keep asking both endpoints on this cadence
// until something activates or the window closes.
const STARTUP_POLL_MS = 10_000;
const MAX_STARTUP_POLLS = 12;

const noop = () => {};

// A sync row created this long before the connection's attempt launched
// belongs to an earlier connection; the margin covers clock skew between
// browser and server.
const EARLIER_CONNECTION_ROW_TOLERANCE_MS = 60_000;

/** Drops the published sync error code, keeping the state when there is none. */
function withoutSyncErrorCode(state: tProfilingState): tProfilingState {
  return state.syncErrorCode === undefined
    ? state
    : { ...state, syncErrorCode: undefined };
}

/**
 * Single owner of the WhatsApp sync → profiling state machine.
 *
 * Mounted once so one socket message drives one state transition, one poll
 * set and one mount-time fetch pair, however many surfaces read the progress
 * (navbar indicator, sync toast, pipe overlay).
 */
export default function ProfilingProgressProvider(props: tProfilingProgressProviderProps) {
  const { currentWorkspaceId } = useWorkspaceContext();
  const { featureFlags } = useFeatureFlagsContext();

  // RET-1393: gate solely on `leads_auto-profiling`. The previous all-three
  // gate also required `onboarding_auto-set-up` (= isPaid && isNew), which
  // flips false the instant onboarding completes — exactly when RET-1386
  // lands the user on /pipe — so the overlay could never activate post-
  // onboarding. `leads_auto-profiling` already implies isPaid and carries the
  // RET-1390 recent-onboarding window, so it is the correct single authority.
  const allFlagsEnabled = featureFlags['leads_auto-profiling'];

  const {
    profilingStartedEvent,
    profilingCompletedEvent,
    profileUpdatedEvent,
    syncContactsCreatedEvent,
    syncContactsUpdatedEvent,
    syncChunksCompleteEvent,
    catchUpEvent,
  } = useSocketsContext();

  const [state, setState] = useState<tProfilingState>({
    active: false,
    total: 0,
    profiled: 0,
    funnelId: null,
    scene: 'contacts',
    globalProgress: 0,
  });

  const profiledRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionRef = useRef(0);
  const historyTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const syncPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const syncPollAttemptsRef = useRef(0);
  const contactsSceneShownRef = useRef(false);
  const startupPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startupPollAttemptsRef = useRef(0);
  // Mirror of state.active for the startup poll's interval closure, which
  // would otherwise read the mount-time snapshot forever.
  const activeRef = useRef(false);
  // When the attempt of the latest re-checked connection launched; null
  // until a re-check runs.
  const connectionLaunchedAtRef = useRef<number | null>(null);

  const clearHistoryTimer = () => {
    if (historyTimerRef.current) {
      clearInterval(historyTimerRef.current);
      historyTimerRef.current = null;
    }
  };

  const clearSyncPoll = () => {
    if (syncPollRef.current) {
      clearInterval(syncPollRef.current);
      syncPollRef.current = null;
    }
  };

  const clearStartupPoll = () => {
    if (startupPollRef.current) {
      clearInterval(startupPollRef.current);
      startupPollRef.current = null;
    }
  };

  const startHistoryTimer = () => {
    clearHistoryTimer();
    historyTimerRef.current = setInterval(() => {
      setState((prev) => {
        if (prev.scene !== 'history' || prev.globalProgress >= HISTORY_PROGRESS_CAP) {
          clearHistoryTimer();
          return prev;
        }
        return { ...prev, globalProgress: prev.globalProgress + 1 };
      });
    }, HISTORY_TICK_MS);
  };

  // The one place that decides which sync error code is published, for the
  // mount fetch, the startup poll, the re-check and the history poll alike.
  const publishSyncErrorCode = useCallback(
    (row: Pick<tSyncStatusResponse['result'], 'created_at' | 'error_code'>) => {
      // Until a reconnect's own row exists, the endpoint answers with the
      // previous connection's row, whose code says nothing about this one.
      const connectionLaunchedAt = connectionLaunchedAtRef.current;
      const isEarlierConnectionRow =
        connectionLaunchedAt !== null &&
        row.created_at !== undefined &&
        new Date(row.created_at).getTime() <
          connectionLaunchedAt - EARLIER_CONNECTION_ROW_TOLERANCE_MS;
      const publishedErrorCode = isEarlierConnectionRow
        ? undefined
        : row.error_code;

      // Published whatever the row's phase, so a failed history row still
      // tells why it failed (2593109: the person did not share the history).
      setState((prev) =>
        prev.syncErrorCode === publishedErrorCode
          ? prev
          : { ...prev, syncErrorCode: publishedErrorCode },
      );
    },
    [],
  );

  const fetchSyncStatus = useCallback(async (): Promise<tProfilingState['scene'] | null> => {
    if (!currentWorkspaceId) return null;

    try {
      const data = await AxiosService.get<tSyncStatusResponse>(
        `whatsapp-api/workspaces/${currentWorkspaceId}/sync/status`,
      );

      const { sync_type, sync_status, last_chunk_received_at, updated_at } =
        data.result;
      publishSyncErrorCode(data.result);

      if (sync_type === 'CONTACT' && ['PENDING', 'IN_PROGRESS'].includes(sync_status)) {
        setState((prev) => ({ ...prev, active: true, scene: 'contacts', globalProgress: 5 }));
        return 'contacts';
      }

      if (sync_type === 'HISTORY' && ['PENDING', 'IN_PROGRESS'].includes(sync_status)) {
        // RET-1511: skip the history phase for a frozen row — one whose last
        // chunk (or last update, if no chunk arrived) is older than the stale
        // window. Surfacing it would make the bar reappear on every reload and
        // linger until the poll ceiling resolves it. A genuinely in-flight sync
        // has a recent timestamp and still shows normally.
        const lastActivity = last_chunk_received_at ?? updated_at;
        const isStale = lastActivity
          ? Date.now() - new Date(lastActivity).getTime() > HISTORY_STALE_MS
          : false;
        if (isStale) return null;

        setState((prev) => ({ ...prev, active: true, scene: 'history', globalProgress: 33 }));
        startHistoryTimer();
        return 'history';
      }

      return null;
    } catch (error) {
      // RET-1393: was a silent `catch { return null }` — every sync-status
      // failure was invisible, which is why a broken pipeline looked like
      // "nothing happens". Log it; the null return remains the graceful
      // fallback (no overlay, board stays usable).
      LLog.error(
        {
          component: LOG_COMPONENTS.API,
          method: 'ProfilingProgressProvider.fetchSyncStatus',
          operation: LOG_OPERATIONS.API_CALL,
        },
        'Profiling: failed to fetch WhatsApp sync status',
        error instanceof Error ? error : new Error(String(error)),
        { workspaceId: currentWorkspaceId },
      );
      // No row (404): there is no code, so an earlier connection's code must
      // not stand for this one. A failed request says nothing about the row,
      // so the published code stays.
      if (isAxiosError(error) && error.response?.status === 404) {
        setState(withoutSyncErrorCode);
      }
      return null;
    }
  }, [currentWorkspaceId, publishSyncErrorCode]);

  const fetchProfilingStatus = useCallback(async (): Promise<boolean> => {
    if (!currentWorkspaceId) return false;

    try {
      const data = await AxiosService.get<tProfilingStatusResponse>(
        `workspace/${currentWorkspaceId}/profiling-status`,
      );

      profiledRef.current = data.profiled;

      if (data.active) {
        const progress = data.total > 0
          ? 66 + Math.round((data.profiled / data.total) * 34)
          : 66;

        // Spread so the sync's error code outlives the move to profiling.
        setState((prev) => ({
          ...prev,
          active: true,
          total: data.total,
          profiled: data.profiled,
          funnelId: data.funnel_id,
          scene: 'profiling',
          globalProgress: progress,
        }));
        return true;
      }

      return false;
    } catch (error) {
      // RET-1393: this is the WS-mount-race recovery path; swallowing its
      // failure silently meant a missed completion event could never be
      // recovered. Log it (the false return keeps the graceful fallback).
      LLog.error(
        {
          component: LOG_COMPONENTS.API,
          method: 'ProfilingProgressProvider.fetchProfilingStatus',
          operation: LOG_OPERATIONS.API_CALL,
        },
        'Profiling: failed to fetch profiling status',
        error instanceof Error ? error : new Error(String(error)),
        { workspaceId: currentWorkspaceId },
      );
      return false;
    }
  }, [currentWorkspaceId]);

  // RET-1394: resolve a history phase that will never receive a terminal
  // signal (no HISTORY sync row → 404, a FAILED status, or the poll ceiling).
  // Hand off to profiling if it has already started so its own 66→100% path
  // drives the bar; otherwise close the indicator at 100%.
  const resolveHistoryStall = useCallback(async () => {
    clearSyncPoll();
    const isProfiling = await fetchProfilingStatus();
    if (!isProfiling) {
      setState((prev) => ({ ...prev, active: false, globalProgress: 100 }));
    }
  }, [fetchProfilingStatus]);

  useEffect(() => {
    activeRef.current = state.active;
  }, [state.active]);

  // RET-1975: while nothing is active yet, re-check both endpoints on a
  // bounded cadence. Stops on activation (any path flips activeRef), on the
  // ceiling, on dismiss, and on unmount/workspace change via effect cleanup.
  const startStartupPoll = useCallback(() => {
    clearStartupPoll();
    startupPollAttemptsRef.current = 0;
    startupPollRef.current = setInterval(async () => {
      if (activeRef.current) {
        clearStartupPoll();
        return;
      }
      startupPollAttemptsRef.current += 1;
      if (startupPollAttemptsRef.current > MAX_STARTUP_POLLS) {
        clearStartupPoll();
        return;
      }
      const isProfilingActive = await fetchProfilingStatus();
      if (isProfilingActive) {
        clearStartupPoll();
        return;
      }
      const scene = await fetchSyncStatus();
      if (scene) clearStartupPoll();
    }, STARTUP_POLL_MS);
  }, [fetchProfilingStatus, fetchSyncStatus]);

  // On mount: determine current state from both endpoints (fallback for navigation)
  useEffect(() => {
    if (!allFlagsEnabled) return;
    const init = async () => {
      const isProfilingActive = await fetchProfilingStatus();
      if (isProfilingActive) return;
      const scene = await fetchSyncStatus();
      if (scene) return;
      startStartupPoll();
    };
    init();
    return () => clearStartupPoll();
  }, [fetchProfilingStatus, fetchSyncStatus, allFlagsEnabled, startStartupPoll]);

  // Handle sync contacts created → scene 1 (contacts sync started)
  syncContactsCreatedEvent.useSubscription(() => {
    if (!allFlagsEnabled) return;
    contactsSceneShownRef.current = true;
    setState((prev) => ({ ...prev, active: true, scene: 'contacts', globalProgress: 5 }));
  });

  // Handle sync contacts updated → progress for scene 1
  syncContactsUpdatedEvent.useSubscription((event) => {
    if (!event || !allFlagsEnabled) return;

    if (event.sync_status === 'COMPLETED') {
      if (!contactsSceneShownRef.current) {
        // Contacts already existed — show scene 1 briefly before transitioning
        contactsSceneShownRef.current = true;
        setState((prev) => ({ ...prev, active: true, scene: 'contacts', globalProgress: 30 }));
        setTimeout(() => {
          setState((prev) => {
            if (prev.scene !== 'contacts') return prev;
            startHistoryTimer();
            return { ...prev, scene: 'history', globalProgress: 33 };
          });
        }, 2500);
        return;
      }
      setState((prev) => ({ ...prev, scene: 'history', globalProgress: 33 }));
      startHistoryTimer();
      return;
    }

    contactsSceneShownRef.current = true;
    const progress = Math.round((event.percentage / 100) * 33);
    setState((prev) => ({ ...prev, active: true, scene: 'contacts', globalProgress: Math.max(progress, 1) }));
  });

  // Handle sync chunks complete → history done, waiting for profiling
  syncChunksCompleteEvent.useSubscription(() => {
    if (!allFlagsEnabled) return;
    clearHistoryTimer();
    setState((prev) => ({ ...prev, active: true, scene: 'history', globalProgress: 66 }));
  });

  // Handle profiling_event.started → transition to profiling scene
  profilingStartedEvent.useSubscription((event) => {
    if (!event?.response || !allFlagsEnabled) return;

    clearHistoryTimer();
    sessionRef.current += 1;
    profiledRef.current = 0;

    setState((prev) => ({
      ...prev,
      active: true,
      total: event.response.total_leads,
      profiled: 0,
      funnelId: String(event.response.funnel_id),
      scene: 'profiling',
      globalProgress: 66,
    }));
  });

  // Handle profile_event.updated — count locally with throttle
  profileUpdatedEvent.useSubscription(() => {
    if (!allFlagsEnabled) return;
    const currentSession = sessionRef.current;

    profiledRef.current += 1;

    if (!timerRef.current) {
      setState((prev) => {
        const progress = prev.total > 0
          ? 66 + Math.round((profiledRef.current / prev.total) * 34)
          : prev.globalProgress;
        return { ...prev, profiled: profiledRef.current, globalProgress: progress };
      });

      timerRef.current = setTimeout(() => {
        timerRef.current = null;

        if (sessionRef.current !== currentSession) return;

        setState((prev) => {
          const progress = prev.total > 0
            ? 66 + Math.round((profiledRef.current / prev.total) * 34)
            : prev.globalProgress;
          return { ...prev, profiled: profiledRef.current, globalProgress: progress };
        });
      }, THROTTLE_MS);
    }
  });

  // Handle profiling_event.completed
  profilingCompletedEvent.useSubscription((event) => {
    if (!event?.response || !allFlagsEnabled) return;

    const { total } = event.response;

    setState((prev) => {
      const resolvedTotal = total ?? prev.total;
      profiledRef.current = resolvedTotal;

      return {
        ...prev,
        active: false,
        profiled: resolvedTotal,
        globalProgress: 100,
      };
    });
  });

  // Reconcile on socket reconnection
  catchUpEvent.useSubscription(async () => {
    if (!allFlagsEnabled) return;
    const isProfilingActive = await fetchProfilingStatus();
    if (!isProfilingActive) {
      await fetchSyncStatus();
    }
  });

  // Poll sync status when history progress caps out (WebSocket event may have been missed)
  useEffect(() => {
    if (!allFlagsEnabled || !state.active || state.scene !== 'history' || state.globalProgress < HISTORY_PROGRESS_CAP) {
      clearSyncPoll();
      return;
    }

    if (syncPollRef.current) return;

    syncPollAttemptsRef.current = 0;
    syncPollRef.current = setInterval(async () => {
      if (!currentWorkspaceId) return;

      // RET-1394: hard ceiling so the bar can never sit at 60% indefinitely.
      // When neither a chunks.complete event nor a COMPLETED status arrives
      // within the ceiling, resolve gracefully (handing off to profiling if
      // it began).
      syncPollAttemptsRef.current += 1;
      if (syncPollAttemptsRef.current > MAX_SYNC_POLLS) {
        LLog.warn(
          {
            component: LOG_COMPONENTS.API,
            method: 'ProfilingProgressProvider.syncPoll',
            operation: LOG_OPERATIONS.API_CALL,
          },
          'Profiling: history sync poll ceiling reached, resolving indicator',
          { workspaceId: currentWorkspaceId, attempts: syncPollAttemptsRef.current },
        );
        await resolveHistoryStall();
        return;
      }

      try {
        const data = await AxiosService.get<tSyncStatusResponse>(
          `whatsapp-api/workspaces/${currentWorkspaceId}/sync/status`,
        );
        const { sync_status } = data.result;
        publishSyncErrorCode(data.result);
        // COMPLETED → history done. FAILED → it will never complete; both are
        // terminal for the indicator.
        if (sync_status === 'COMPLETED' || sync_status === 'FAILED') {
          await resolveHistoryStall();
        }
        // PENDING / IN_PROGRESS → keep polling until the ceiling.
      } catch (error) {
        // RET-1394: a 404 is the contractual "no HISTORY sync exists" answer —
        // Meta delivered no Cloud-API history for this WABA, so there is
        // nothing to download and `chunks.complete` will never fire. Treat it
        // as terminal rather than a transport failure to retry (mirrors the
        // mount-time 404 contract). This was the root cause of the 60% stall.
        if (isAxiosError(error) && error.response?.status === 404) {
          setState(withoutSyncErrorCode);
          await resolveHistoryStall();
          return;
        }
        // RET-1393: other poll errors are retried on the next interval, but
        // were previously invisible — log at warn so a persistently failing
        // poll is observable rather than a silent stall.
        LLog.warn(
          {
            component: LOG_COMPONENTS.API,
            method: 'ProfilingProgressProvider.syncPoll',
            operation: LOG_OPERATIONS.API_CALL,
          },
          'Profiling: sync-status poll failed (will retry next interval)',
          {
            workspaceId: currentWorkspaceId,
            error: error instanceof Error ? error.message : String(error),
          },
        );
      }
    }, SYNC_POLL_MS);
  }, [
    state.active,
    state.scene,
    state.globalProgress,
    currentWorkspaceId,
    resolveHistoryStall,
    publishSyncErrorCode,
  ]);

  // A connection made after the mount-time poll ran out creates its sync row
  // later, and nothing else asks for it until a reload.
  const recheckSyncStatus = useCallback(
    (connectionLaunchedAt: number) => {
      // A re-check follows a new connection, whose sync has not answered yet.
      connectionLaunchedAtRef.current = connectionLaunchedAt;
      setState(withoutSyncErrorCode);
      if (activeRef.current) return;
      const recheck = async () => {
        const isProfilingActive = await fetchProfilingStatus();
        if (isProfilingActive) return;
        const scene = await fetchSyncStatus();
        if (scene) return;
        startStartupPoll();
      };
      void recheck();
    },
    [fetchProfilingStatus, fetchSyncStatus, startStartupPoll],
  );

  const dismiss = useCallback(() => {
    clearHistoryTimer();
    clearSyncPoll();
    clearStartupPoll();
    setState((prev) => ({ ...prev, active: false, globalProgress: 100 }));
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      clearHistoryTimer();
      clearSyncPoll();
      clearStartupPoll();
    };
  }, []);

  const value = useMemo<tProfilingProgressContext>(
    () =>
      allFlagsEnabled
        ? { ...state, dismiss, recheckSyncStatus }
        : {
            ...INACTIVE_PROFILING_STATE,
            dismiss: noop,
            recheckSyncStatus: noop,
          },
    [allFlagsEnabled, state, dismiss, recheckSyncStatus],
  );

  return (
    <ProfilingProgressContext.Provider value={value}>
      {props.children}
    </ProfilingProgressContext.Provider>
  );
}
